import { h } from 'preact';
import { useState, useEffect, Suspense, lazy } from 'preact/compat';
import './app.scss';

import CookieNotice from './components/CookieNotice';
import { LanguageSwitcher } from './components/LanguageSwitcher';

import { BrowserNotSupported } from './views/BrowserNotSupported';
import { Welcome } from './views/Welcome';

import { isChrome, isMobile } from './utils/env';
import { track } from './utils/analytics';

// Lazy load the components that are not entry points
const OpenDevTools = lazy(() => import('./views/OpenDevTools').then(module => ({ default: module.OpenDevTools })));
const OpenAiAssistance = lazy(() => import('./views/OpenAiAssistance').then(module => ({ default: module.OpenAiAssistance })));
const EnableAiAssistance = lazy(() => import('./views/EnableAiAssistance').then(module => ({ default: module.EnableAiAssistance })));
const RecordTrace = lazy(() => import('./views/RecordTrace').then(module => ({ default: module.RecordTrace })));
const Demo = lazy(() => import('./views/Demo').then(module => ({ default: module.Demo })));

// Preloader function
const preload = {
  'intro': () => import('./views/OpenDevTools'),
  'open-devtools': () => import('./views/OpenAiAssistance'),
  'open-ai-assistance': () => import('./views/EnableAiAssistance'),
  'enable-ai-assistance': () => import('./views/RecordTrace'),
  'record-trace': () => import('./views/Demo'),
};

// Basic browser detection
const isSupported = isChrome() && !isMobile();

const getStepFromHash = () => window.location.hash.replace('#', '');

export function App() {
  const [step, setStep] = useState(() => {
    const hashStep = getStepFromHash();

    if (!isSupported && (hashStep == 'intro' || hashStep == 'open-devtools')) {
      return 'unsupported';
    }

    let validSteps = ['intro', 'open-devtools', 'open-ai-assistance', 'enable-ai-assistance', 'record-trace', 'demo', 'congratulations'];
    if (validSteps.includes(hashStep)) {
      return hashStep;
    }

    return 'intro';
  });

  // Preload the next step's component
  useEffect(() => {
    if (preload[step]) {
      preload[step]();
    }
  }, [step]);

  // Analytics for step changes
  useEffect(() => {
    track(`${step}_viewed`);

    const handleBeforeUnload = () => {
      if (step === 'record-trace') {
        // This is an expected reload as part of the flow, not an exit.
        track('record_trace');
        localStorage.setItem('reloaded_from_record_trace', 'true');
      } else {
        track('flow_exited', { last_step: step });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [step]);


  // Effect to listen for hash changes (e.g., browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const newStep = getStepFromHash();
      if (newStep && newStep !== step) {
        setStep(newStep);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [step]);

  // Navigation function to update state and hash
  const navigateTo = (newStep, action) => {
    // After the 'open-devtools' step, we can assume the user is in a valid environment,
    // even if device emulation makes `isMobile()` return true.
    const stepsWhereEmulationIsAllowed = ['open-ai-assistance', 'enable-ai-assistance', 'record-trace', 'demo', 'congratulations'];
    const isNavigatingToSafeStep = stepsWhereEmulationIsAllowed.includes(newStep);

    let targetStep = newStep;
    if (!isSupported && !isNavigatingToSafeStep) {
      targetStep = newStep === 'intro' ? 'intro' : 'unsupported';
    }

    if (getStepFromHash() !== targetStep) {
      window.location.hash = targetStep;
    }

    if (action) {
      track(action, { current_step: step, target_step: newStep });
    }

    setStep(targetStep);
  };
  
  // Set initial hash
  useEffect(() => {
    if (getStepFromHash() !== step) {
      window.location.hash = step;
    }
  }, []);

  useEffect(() => {
    const reloadedFromRecordTrace = localStorage.getItem('reloaded_from_record_trace');
    if (reloadedFromRecordTrace) {
      localStorage.removeItem('reloaded_from_record_trace'); // Only run once
      navigateTo('demo');
    }
  }, []);

  const renderStep = () => {
    switch (step) {
      case 'intro':
        return <Welcome onGetStarted={() => navigateTo('open-devtools', 'start_flow')}  />;
      case 'unsupported':
        return <BrowserNotSupported onBack={() => navigateTo('intro', 'flow_back')} isChrome={isChrome} isMobile={isMobile} />;
      case 'open-devtools':
        return <OpenDevTools onBack={() => navigateTo('intro', 'flow_back')} onDevToolsOpened={() => navigateTo('open-ai-assistance', 'continue_flow')} />;
      case 'open-ai-assistance':
        return <OpenAiAssistance onBack={() => navigateTo('intro', 'flow_back')} onComplete={() => navigateTo('enable-ai-assistance', 'continue_flow')} />;
      case 'enable-ai-assistance':
        return <EnableAiAssistance onBack={() => navigateTo('open-ai-assistance', 'flow_back')} onEnabled={() => navigateTo('record-trace', 'continue_flow')} />;
      case 'record-trace':
        return <RecordTrace onBack={() => navigateTo('enable-ai-assistance', 'flow_back')} onComplete={() => navigateTo('demo', 'continue_flow')} />;
      case 'demo':
        return <Demo onBack={() => navigateTo('record-trace', 'flow_back')} onComplete={() => navigateTo('congratulations', 'continue_flow')} />;
      default:
        navigateTo('intro');
        return null;
    }
  };

  return (
    <>
      <Suspense fallback={<div />}>
        {renderStep()}
      </Suspense>
      <footer>
        <CookieNotice />
        <LanguageSwitcher />
      </footer>
    </>
  );
}
